import { IconProps } from 'common/components/Icon';
import variables from 'common/config/variables';
import { getScalablePixel } from 'common/utils';
import GlobalStyle from 'common/utils/GlobalStyle';
import { isNil } from 'lodash';
import React, { RefObject } from 'react';
import styled, { css } from 'styled-components';
const { font_color_white } = variables;

export type FlexAlign =
  | 'space-between'
  | 'space-around'
  | 'center'
  | 'flex-end'
  | 'flex-start'
  | 'initial';

export interface FocusableStyledComponentProps {
  focused?: boolean;
}

export interface Flex {
  justifyContent?: FlexAlign;
  alignItems?: FlexAlign;
  flex?: number | string;
  gap?: number;
  alignSelf?: 'center' | 'flex-start' | 'flex-end' | 'initial';
}

export interface CommonStyle extends Flex {
  maxHeight?: string;
  maxWidth?: string;
  minWidth?: string;
  padding?: string;
  textAlign?: 'center' | 'left' | 'right';
  paddingRight?: number;
  paddingLeft?: number;
  paddingTop?: number;
  paddingBottom?: number;
  margin?: string;
  marginRight?: number;
  marginLeft?: number;
  marginTop?: number;
  marginBottom?: number;
  width?: string | number;
  height?: string | number;
  backgroundColor?: string;
  borderRadius?: number;
  borderColor?: string;
  borderWidth?: number;
  borderStyle?: string;
  boxShadow?: string;
  opacity?: number;
  overflow?: string;
  cursor?: string;
  color?: string;
  // TODO: add more in the future
  ref?: RefObject<HTMLDivElement | null>;
}

GlobalStyle.inject`
  .row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .column {
    display: flex;
    flex-direction: column;
  }
`;

export const Row = (
  props: React.PropsWithChildren<CommonStyle & React.HTMLAttributes<HTMLDivElement>>
) => (
  <div
    {...props}
    className={'row' + (props.className ? ` ${props.className}` : '')}
    style={{ ...props }}
  />
);

export const Column = (
  props: React.PropsWithChildren<CommonStyle & React.HTMLAttributes<HTMLDivElement>>
) => (
  <div
    {...props}
    className={'column' + (props.className ? ` ${props.className}` : '')}
    style={{ ...props }}
  />
);

export const FlexRowMixin = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const IconSizeMap = {
  miniature: 10,
  verySmall: 16,
  small: 24,
  medium: 36,
  large: 48,
  veryLarge: 96
};

export const IconStyle = ({ size }: IconProps) => `
  width: ${getScalablePixel(IconSizeMap[size])}px;
`;

export const VerticalSeparator = styled.div<{ height?: string; margin?: number }>`
  width: ${getScalablePixel(1)}px;
  height: ${({ height }) => (isNil(height) ? '80%' : height)};
  margin: 0 ${({ margin }) => getScalablePixel(margin || 2)}px;
  opacity: 0.5;
  background-color: #fff;
`;

export const RadioButton = styled(Row)<{ checked?: boolean }>`
  margin: 0 ${getScalablePixel(10)}px;
  width: ${getScalablePixel(28)}px;
  height: ${getScalablePixel(28)}px;
  border-radius: ${getScalablePixel(28)}px;
  background: transparent;
  border: solid ${getScalablePixel(3)}px #fff;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  &:after {
    content: '';
    left: 0;
    top: 0;
    width: ${getScalablePixel(12)}px;
    height: ${getScalablePixel(12)}px;
    background: ${({ checked }) => (checked ? font_color_white : 'transparent')};
    border-radius: ${getScalablePixel(10)}px;
  }
`;

export const LoadingIndicator = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  margin: auto;
  z-index: 1;
  width: ${getScalablePixel(120)}px;
  height: ${getScalablePixel(120)}px;
  border: ${getScalablePixel(12)}px solid #fff;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;

  @keyframes rotation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
